import axios from 'axios'
import { useAuthStore } from '@/store/authStore'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Token refresh mutex
let isRefreshing = false
let refreshPromise: Promise<string> | null = null

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = useAuthStore.getState().refreshToken
      if (refreshToken) {
        if (!isRefreshing) {
          isRefreshing = true
          refreshPromise = axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          })
            .then((response) => {
              const { access_token, refresh_token } = response.data
              const user = useAuthStore.getState().user

              if (user) {
                useAuthStore.getState().setAuth(user, access_token, refresh_token)
              }

              isRefreshing = false
              refreshPromise = null
              return access_token as string
            })
            .catch(() => {
              isRefreshing = false
              refreshPromise = null
              useAuthStore.getState().logout()
              return Promise.reject(error)
            })
        }

        return refreshPromise!.then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        })
      }
    }

    return Promise.reject(error)
  }
)

export default api
